import { FC, useMemo } from "react";
import { mockPriceHistory, currentPrice, priceChange24h, priceUsd } from "@/lib/mockData";
import {
  buildPriceChartViewModel,
  DESKTOP_TIME_LABELS,
  PriceChartData,
} from "./priceChartModel";
import type { Market } from "@module-bindings/types";
import { formatMarketPairLabel } from "@/lib/marketUtils";
import { TradingPaneState } from "./TradingPaneState";

interface PriceChartProps {
  compact?: boolean;
  immersiveMobile?: boolean;
  selectedMarket: Market | null;
}

export const PriceChart: FC<PriceChartProps> = ({
  compact = false,
  immersiveMobile = false,
  selectedMarket,
}) => {
  const chartData = useMemo<PriceChartData>(
    () => ({
      currentPrice,
      pairLabel: selectedMarket ? formatMarketPairLabel(selectedMarket) : "No Markets",
      priceChange24h,
      priceUsd,
      history: mockPriceHistory,
    }),
    [selectedMarket],
  );

  const {
    areaPath,
    gridLines,
    padding,
    path,
    strokeColor,
    svgHeight,
    svgWidth,
  } = useMemo(() => buildPriceChartViewModel(chartData, compact), [chartData, compact]);

  const timeRanges = ["1m", "5m", "15m", "1H", "4H", "1D"] as const;

  if (!selectedMarket) {
    return (
      <div className="sr-panel flex h-full min-h-[20rem] flex-col">
        <TradingPaneState
          eyebrow="Market View"
          title="Select a market"
          message="Choose a trading pair to load the chart and market context."
        />
      </div>
    );
  }

  return (
    <section
      className={`flex h-full flex-col overflow-hidden rounded-lg bg-sr-surface ${
        compact
          ? ""
          : immersiveMobile
            ? "min-h-[58vh] flex-1"
            : "min-h-0 flex-1"
      }`}
      aria-label={`${chartData.pairLabel} price chart`}
    >
      {!compact && (
        <div className="flex items-center justify-between border-b border-sr-border-subtle/50 px-4 py-2">
          <div className="flex items-center gap-1" role="tablist" aria-label="Chart time range">
            {timeRanges.map((range) => (
              <button
                key={range}
                role="tab"
                aria-selected={range === "1H"}
                className={`sr-focus-ring rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors ${
                  range === "1H"
                    ? "bg-sr-neutral-900 text-sr-lemon-muted"
                    : "text-sr-neutral-500 hover:bg-white/[0.03] hover:text-sr-neutral-300"
                }`}
              >
                {range}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 border-l border-sr-border-subtle/50 pl-3">
            <button className="sr-focus-ring rounded-md p-2 text-sr-neutral-500 transition-colors hover:bg-sr-neutral-900 hover:text-sr-neutral-300" aria-label="Line chart view">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M4 13L7.5 6L10 10L14 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button className="sr-focus-ring rounded-md p-2 text-sr-neutral-500 transition-colors hover:bg-sr-neutral-900 hover:text-sr-neutral-300" aria-label="Candlestick view">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <rect x="3" y="5" width="3" height="8" rx="0.5" stroke="currentColor" strokeWidth="1.2" />
                <rect x="7.5" y="3" width="3" height="12" rx="0.5" stroke="currentColor" strokeWidth="1.2" />
                <rect x="12" y="7" width="3" height="6" rx="0.5" stroke="currentColor" strokeWidth="1.2" />
              </svg>
            </button>
            <button className="sr-focus-ring rounded-md p-2 text-sr-neutral-500 transition-colors hover:bg-sr-neutral-900 hover:text-sr-neutral-300" aria-label="Analytics view">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M3 3V15H15" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                <path d="M6 12L9 8L12 10L15 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Chart */}
      <div
        className={
          compact
            ? "px-2 pb-2"
            : immersiveMobile
              ? "relative flex-1 px-1 pt-3 pb-2"
              : "relative min-h-0 flex-1 px-1 pt-2 pb-1"
        }
      >
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className={
            compact
              ? "w-full"
              : immersiveMobile
                ? "h-[46vh] min-h-[320px] w-full"
                : "h-full w-full"
          }
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="chartAreaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={strokeColor} stopOpacity="0.08" />
              <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {gridLines.map((gl, i) => (
            <g key={i}>
              <line
                x1={padding.left}
                y1={gl.y}
                x2={svgWidth - padding.right}
                y2={gl.y}
                stroke="var(--color-sr-border-subtle)"
                strokeWidth="0.5"
                strokeDasharray="4 4"
                opacity="0.3"
              />
            </g>
          ))}

          {/* Area fill */}
          <path d={areaPath} fill="url(#chartAreaGrad)" />

          {/* Line */}
          <path
            d={path}
            fill="none"
            stroke={strokeColor}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {!compact && (
          <div className="pointer-events-none absolute inset-y-0 right-3">
            {gridLines.map((gl, i) => (
              <span
                key={i}
                className="absolute right-0 -translate-y-1/2 text-[11px] text-sr-neutral-500"
                style={{
                  top: `${(gl.y / svgHeight) * 100}%`,
                  fontFamily: "var(--font-display)",
                }}
              >
                {gl.label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* X-axis time labels (desktop only) */}
      {!compact && (
        <div className="flex justify-between px-4 pb-3 text-[10px] text-sr-neutral-500 tabular-nums">
          {DESKTOP_TIME_LABELS.map((t, i) => (
            <span key={i}>{t}</span>
          ))}
        </div>
      )}
    </section>
  );
};
